<template>
  <div class="vt-timeline">
    <!-- Cabeçalho -->
    <div class="vt-tl-header">
      <div class="vt-tl-titles">
        <h3 class="vt-tl-title">{{ titulo }}</h3>
        <p v-if="subtitulo" class="vt-tl-sub">{{ subtitulo }}</p>
      </div>
      <span class="vt-tl-count">{{ totalRegistros }} {{ totalRegistros === 1 ? 'registro' : 'registros' }}</span>
    </div>

    <!-- Filtros -->
    <div class="vt-tl-filters">
      <span class="vt-search">
        <fa-icon icon="search" />
        <input
          type="text"
          v-model="busca"
          placeholder="Buscar por colaborador, ticket ou campo..."
        />
      </span>
      <select v-model="filtroAcao" class="vt-select">
        <option value="">Todas as ações</option>
        <option v-for="a in acoesDisponiveis" :key="a" :value="a">{{ a }}</option>
      </select>
      <select v-model="filtroColab" class="vt-select">
        <option value="">Todos os colaboradores</option>
        <option v-for="c in colaboradoresDisponiveis" :key="c.id" :value="c.id">{{ c.nome }}</option>
      </select>
    </div>

    <!-- A paginação é server-side, mas busca/filtros atuam só nos itens já
         carregados: com mais de uma página, deixamos isso explícito. -->
    <p v-if="filtroAtivo && multiPagina" class="vt-tl-aviso-filtro">
      <fa-icon icon="info-circle" />
      Filtrando apenas os registros da página atual — as demais páginas não são pesquisadas.
    </p>

    <!-- Corpo rolável -->
    <div class="vt-tl-body">
      <div v-if="!itensFiltrados.length" class="vt-tl-empty">
        <template v-if="filtroAtivo">
          Nenhum resultado nesta página —
          {{ multiPagina ? 'navegue entre as páginas ou limpe o filtro.' : 'limpe o filtro para ver todos os registros.' }}
        </template>
        <template v-else>
          Nenhuma alteração registrada para este registro.
        </template>
      </div>

      <template v-else>
        <LogAlteracaoItem
          v-for="(item, idx) in itensFiltrados"
          :key="item.id || idx"
          :item="item"
          :is-last="idx === itensFiltrados.length - 1"
          :restorable="restorable"
          :field-labels="fieldLabels"
          @restaurar="(sql) => $emit('restaurar', sql)"
          @ver-sql="abrirSql"
          @ver-diff="abrirDiff"
        />

        <pagination
          v-if="paginator && paginator.last_page > 1"
          :data="paginator"
          :limit="2"
          @pagination-change-page="(p) => $emit('page', p)"
        />
      </template>
    </div>

    <!-- Visualizador de SQL (atalho "Ver SQL") -->
    <modal
      :name="sqlModalName"
      height="auto"
      width="80%"
      :lazy="true"
      :scrollable="true"
      :clickToClose="true"
    >
      <CloseModal :close="fecharSql" />
      <div class="modalbox">
        <div class="modalbox-content">
          <h5 class="vt-sql-title">{{ sqlTitulo }}</h5>
          <div class="vt-sql-editor-wrap">
            <SqlEditor :value="sqlAtual || ''" :readonly="true" />
          </div>
        </div>
      </div>
      <div class="vt-sql-footer">
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="copiarSql">
          Copiar SQL
        </button>
        <button type="button" class="btn btn-sm btn-secondary" @click="fecharSql">
          Fechar
        </button>
      </div>
    </modal>

    <!-- Diff SQL (mesma tela de comparação lado a lado) -->
    <modal
      :name="diffModalName"
      height="auto"
      width="95%"
      :lazy="true"
      :scrollable="true"
      :clickToClose="true"
    >
      <CloseModal :close="fecharDiff" />
      <div class="modalbox">
        <div class="modalbox-content">
          <h5 class="vt-sql-title">Diff SQL{{ diffTitulo ? ' — ' + diffTitulo : '' }}</h5>
          <div class="vt-diff-body">
            <CodeDiff
              :old-string="diffOld || ''"
              :new-string="diffNew || ''"
              output-format="side-by-side"
              :force-inline-comparison="true"
              language="sql"
            />
          </div>
        </div>
      </div>
      <div class="vt-sql-footer">
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="copiarTexto(diffOld)">
          Copiar SQL antigo
        </button>
        <button type="button" class="btn btn-sm btn-outline-primary" @click="copiarTexto(diffNew)">
          Copiar SQL novo
        </button>
        <button type="button" class="btn btn-sm btn-secondary" @click="fecharDiff">
          Fechar
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import CloseModal from "@/components/CloseModal";
import SqlEditor from "@/components/form/SqlEditor.vue";
import LogAlteracaoItem from "@/components/Log/LogAlteracaoItem.vue";
import { CodeDiff } from "v-code-diff";

// Metadados automáticos de save — uma alteração só destes não é relevante no log.
const META_FIELDS = ["datahora_atualizacao", "colaborador_id_atualizacao"];

export default {
  name: "LogAlteracoesTimeline",
  components: { CloseModal, SqlEditor, LogAlteracaoItem, CodeDiff },
  props: {
    itens: { type: Array, default: () => [] },
    paginator: { type: Object, default: () => ({}) },
    total: { type: Number, default: null },
    titulo: { type: String, default: "Histórico de alterações" },
    subtitulo: { type: String, default: "" },
    restorable: { type: Boolean, default: false },
    fieldLabels: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      busca: "",
      filtroAcao: "",
      filtroColab: "",
      sqlAtual: null,
      sqlTitulo: "",
      diffOld: null,
      diffNew: null,
      diffTitulo: "",
    };
  },
  computed: {
    sqlModalName() {
      return `log-sql-view-${this._uid}`;
    },
    diffModalName() {
      return `log-sql-diff-${this._uid}`;
    },
    totalRegistros() {
      return this.total != null ? this.total : this.itens.length;
    },
    acoesDisponiveis() {
      return [...new Set(this.itens.map((i) => String(i.acao || "").toUpperCase()).filter(Boolean))];
    },
    colaboradoresDisponiveis() {
      // Chave = id (dois colaboradores homônimos não podem se misturar na
      // trilha de auditoria); o nome é só o label da opção.
      const porId = new Map();
      for (const i of this.itens) {
        const colab = i.colaborador;
        if (colab && colab.id != null && !porId.has(colab.id)) {
          porId.set(colab.id, { id: colab.id, nome: colab.nome });
        }
      }
      return [...porId.values()];
    },
    filtroAtivo() {
      return !!(this.busca.trim() || this.filtroAcao || this.filtroColab !== "");
    },
    multiPagina() {
      return !!(this.paginator && this.paginator.last_page > 1);
    },
    itensFiltrados() {
      const termo = this.busca.trim().toLowerCase();

      return this.itens
        .filter((i) => {
          // Oculta entradas cuja única alteração é metadado (ex.: só data/hora de atualização).
          if (!this.temAlteracaoReal(i)) return false;
          if (this.filtroAcao && String(i.acao || "").toUpperCase() !== this.filtroAcao) return false;
          if (this.filtroColab !== "" && (!i.colaborador || i.colaborador.id !== this.filtroColab)) return false;
          if (termo && !this.casaBusca(i, termo)) return false;
          return true;
        })
        .slice()
        .sort((a, b) => String(b.datahora_criacao || "").localeCompare(String(a.datahora_criacao || "")));
    },
  },
  methods: {
    temAlteracaoReal(item) {
      const keys = new Set([
        ...Object.keys(item.dados_anteriores || {}),
        ...Object.keys(item.dados_novos || {}),
      ]);
      return [...keys].some((k) => !META_FIELDS.includes(k));
    },
    casaBusca(item, termo) {
      const campos = [
        item.colaborador && item.colaborador.nome,
        item.motivo_ticket,
        item.motivo_descricao,
        item.motivo_alterado_por,
        item.motivo_etapa,
        item.motivo_solicitante,
        ...Object.keys(item.dados_anteriores || {}),
        ...Object.keys(item.dados_novos || {}),
      ];
      return campos.some((c) => c && String(c).toLowerCase().includes(termo));
    },
    abrirSql({ sql, titulo }) {
      this.sqlAtual = sql || "";
      this.sqlTitulo = titulo || "Consulta SQL";
      this.$modal.show(this.sqlModalName);
    },
    fecharSql() {
      this.$modal.hide(this.sqlModalName);
    },
    abrirDiff({ old, new: novo, titulo }) {
      this.diffOld = old || "";
      this.diffNew = novo || "";
      this.diffTitulo = titulo || "";
      this.$modal.show(this.diffModalName);
    },
    fecharDiff() {
      this.$modal.hide(this.diffModalName);
    },
    copiarSql() {
      this.copiarTexto(this.sqlAtual);
    },
    // Copia o texto cru, preservando a formatação original (sem reindentar/normalizar).
    copiarTexto(texto) {
      if (typeof texto === "string" && texto.length) {
        navigator.clipboard.writeText(texto);
      }
    },
  },
};
</script>

<style scoped>
.vt-timeline {
  --vt-navy: #3a3e81;
  --vt-teal: #2298a3;
  --vt-teal-dark: #1a7a83;
  --vt-bg: #eef0f3;
  --vt-card: #ffffff;
  --vt-border: #e4e7ec;
  --vt-border-strong: #d3d8e0;
  --vt-head: #f6f7f9;
  --vt-text: #2c2e38;
  --vt-text-soft: #5a6072;
  --vt-muted: #949aa8;
  --vt-red: #e23b3b;
  --mono: SFMono-Regular, Menlo, Consolas, monospace;
  color: var(--vt-text);
}

/* Cabeçalho */
.vt-tl-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 2px 14px;
}

.vt-tl-title {
  font-size: 20px;
  font-weight: 800;
  margin: 0;
  color: var(--vt-text);
}

.vt-tl-sub {
  margin: 2px 0 0;
  font-size: 12.5px;
  color: var(--vt-text-soft);
}

.vt-tl-count {
  flex-shrink: 0;
  background: #e8ebf5;
  color: var(--vt-navy);
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
  padding: 5px 12px;
  white-space: nowrap;
}

/* Filtros */
.vt-tl-filters {
  display: flex;
  gap: 10px;
  background: var(--vt-head);
  border: 1px solid var(--vt-border);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.vt-search {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 220px;
  background: #fff;
  border: 1px solid var(--vt-border);
  border-radius: 6px;
  padding: 6px 10px;
  color: var(--vt-muted);
}

.vt-search input {
  border: none;
  outline: none;
  flex: 1;
  font-size: 13px;
  color: var(--vt-text);
  background: transparent;
}

.vt-select {
  border: 1px solid var(--vt-border);
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  color: var(--vt-text);
  background: #fff;
  cursor: pointer;
}

/* Aviso: filtro client-side com paginação server-side */
.vt-tl-aviso-filtro {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: -6px 0 10px;
  font-size: 12px;
  color: var(--vt-text-soft);
}

/* Corpo */
.vt-tl-body {
  max-height: 60vh;
  overflow-y: auto;
  padding: 4px 4px 4px 2px;
}

.vt-tl-empty {
  text-align: center;
  color: var(--vt-text-soft);
  padding: 40px 16px;
  font-size: 14px;
}

/* Visualizador de SQL */
.vt-sql-title {
  font-weight: 700;
  margin-bottom: 12px;
}

/* O SqlEditor ocupa 100% da altura do contêiner; damos a ele uma área dimensionada. */
.vt-sql-editor-wrap {
  height: 60vh;
}

.vt-sql-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--vt-border);
  background: #fff;
}

.vt-diff-body {
  max-height: 72vh;
  overflow: auto;
}
</style>
